import React, { useState, useEffect, useContext } from "react";
import { FaMoneyBillWave, FaCheckCircle, FaTimes, FaExclamationTriangle, FaSpinner } from "react-icons/fa";
import "./SalesManager.css";
import API_URL from "../config";
import { AuthContext } from "./AuthContext";

export default function SalesManager({ entrepriseId, siteId }) {
  const { user } = useContext(AuthContext);

  const [produits, setProduits] = useState([]);
  const [clients, setClients] = useState([]);
  const [stocks, setStocks] = useState([]);

  const [loadingProduits, setLoadingProduits] = useState(true);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingStocks, setLoadingStocks] = useState(false);

  const [produitsError, setProduitsError] = useState(null);
  const [clientsError, setClientsError] = useState(null);
  const [stocksError, setStocksError] = useState(null);

  const [selectedProduit, setSelectedProduit] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  const [saleQuantity, setSaleQuantity] = useState("");
  const [prixUnitaire, setPrixUnitaire] = useState("");
  const [multiStockQuantities, setMultiStockQuantities] = useState({});

  const [loadingAction, setLoadingAction] = useState(false);
  // Ancien état submitError est remplacé par customDialog pour l'affichage
  const [successMessage, setSuccessMessage] = useState(null); 

  const [quantityError, setQuantityError] = useState(null);
  const [disableStockSelect, setDisableStockSelect] = useState(false);

  const [remainingToAllocate, setRemainingToAllocate] = useState(0);

  // États pour le Montant et le Paiement
  const [montantVente, setMontantVente] = useState(0);
  const [montantPaye, setMontantPaye] = useState(""); 
  const [clientAutreSelected, setClientAutreSelected] = useState(true);

  // --- NOUVEAU: État pour la boîte de dialogue personnalisée ---
  const [customDialog, setCustomDialog] = useState(null); // { type: 'success'|'error'|'confirm', message: '', action: null, payload: null }

  // Fonction pour afficher la boîte de dialogue
  const showCustomDialog = (type, message, action = null, payload = null) => {
      setCustomDialog({ type, message, action, payload });
  };
  // Fonction pour fermer la boîte de dialogue
  const closeCustomDialog = () => {
      setCustomDialog(null);
  };
  // Fonction pour gérer l'action confirmée
  const handleDialogAction = () => {
      if (customDialog.action) {
          customDialog.action(customDialog.payload);
      }
      closeCustomDialog();
  };


  const fetchStocks = async () => {
    if (!entrepriseId || !siteId) return;
    try {
      setLoadingStocks(true);
      const res = await fetch(`${API_URL}/api/stocks/${entrepriseId}/${siteId}`);
      const data = await res.json();
      setStocks(Array.isArray(data) ? data : []);
    } catch (err) {
      // Afficher l'erreur dans la console, pas de dialogue pour un simple fetch
      setStocksError(err.message); 
    } finally {
      setLoadingStocks(false);
    }
  };

  useEffect(() => {
    if (!entrepriseId || !siteId) return;

    const fetchProduits = async () => {
      try {
        const res = await fetch(`${API_URL}/api/produits?entrepriseId=${entrepriseId}&siteId=${siteId}`);
        const data = await res.json();
        setProduits(Array.isArray(data) ? data : []);
      } catch (err) {
        setProduitsError(err.message);
      } finally {
        setLoadingProduits(false);
      }
    };

    const fetchClients = async () => {
      try {
        const res = await fetch(`${API_URL}/api/clients?entrepriseId=${entrepriseId}&siteId=${siteId}`);
        const data = await res.json();
        setClients(Array.isArray(data) ? data : []);
      } catch (err) {
        setClientsError(err.message);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchProduits();
    fetchClients();
    fetchStocks();
  }, [entrepriseId, siteId]);

  // Calcul du prix unitaire (y compris prix grossiste)
  useEffect(() => {
    let prix = 0;
    if (selectedProduit) {
      prix = Number(selectedProduit.prix_unitaire || 0);

      if (selectedClient && Array.isArray(selectedClient.produits)) {
        const prodLink = selectedClient.produits.find(
          (p) => Number(p.produit_id) === Number(selectedProduit.id)
        );
        if (prodLink && prodLink.prix_grossiste != null) {
          prix = Number(prodLink.prix_grossiste);
        }
      }
    }
    setPrixUnitaire(prix);
  }, [selectedProduit, selectedClient]);

  // Calcul du Montant Total de la Vente
  useEffect(() => {
    const q = Number(saleQuantity || 0);
    const pu = Number(prixUnitaire || 0);
    const total = q * pu;
    setMontantVente(total);
  }, [saleQuantity, prixUnitaire]);

  // Gestion du Montant Payé lors du changement de client ou de la vente totale
  useEffect(() => {
    if (clientAutreSelected) {
      // Préremplir le montant payé pour le client "Autre"
      setMontantPaye(montantVente.toFixed(2));
    } else {
      // Vider le montant payé si un client réel est sélectionné
      setMontantPaye("");
    }
  }, [clientAutreSelected, montantVente]);

  const filteredClients = selectedProduit
    ? clients.filter((c) =>
        Array.isArray(c.produits)
          ? c.produits.some((p) => Number(p.produit_id) === Number(selectedProduit.id) && p.prix_grossiste != null)
          : false
      )
    : [];

  const filteredStocks = (() => {
    if (!selectedProduit) return [];
    const produitStocks = stocks.filter((s) => Number(s.produit_id) === Number(selectedProduit.id));
    const totalStock = produitStocks.reduce((acc, s) => acc + Number(s.stock_restant || 0), 0);
    const arr = [...produitStocks];
    if (produitStocks.length > 0) {
      arr.push({
        id: "all",
        produitDenomination: "Tous les stocks combinés",
        stock_restant: totalStock
      });
    }
    return arr;
  })();

  const totalAvailableForSelectedProduit = (() => {
    if (!selectedProduit) return 0;
    return stocks
      .filter((s) => Number(s.produit_id) === Number(selectedProduit.id))
      .reduce((acc, s) => acc + Number(s.stock_restant || 0), 0);
  })();

  const handleProduitChange = (e) => {
    const id = e.target.value;
    if (!id) {
      setSelectedProduit(null);
      setSelectedClient(null);
      setSelectedStock(null);
      setMultiStockQuantities({});
      setQuantityError(null);
      setDisableStockSelect(false);
      setRemainingToAllocate(0);
      setMontantPaye("");
      return;
    }
    const produit = produits.find((p) => String(p.id) === String(id)) || null;
    setSelectedProduit(produit);
    setSelectedClient(null);
    setSelectedStock(null);
    setMultiStockQuantities({});
    setQuantityError(null);
    setDisableStockSelect(false);
    setRemainingToAllocate(0);
  };

  const handleStockChange = (e) => {
    const id = e.target.value;
    setMultiStockQuantities({});
    setRemainingToAllocate(0);

    if (!id) return setSelectedStock(null);

    const q = Number(saleQuantity || 0);
    if (q > totalAvailableForSelectedProduit) {
      setSelectedStock(null);
      setQuantityError(`Quantité demandée (${q}) supérieure au stock total (${totalAvailableForSelectedProduit}).`);
      setDisableStockSelect(true);
      return;
    }

    if (id === "all") {
      setSelectedStock({ id: "all" });
      setQuantityError(null);
      setDisableStockSelect(false);
      const qInt = Number(saleQuantity || 0);
      const somme = 0;
      setRemainingToAllocate(qInt - somme);
      return;
    }

    const s = stocks.find((s) => String(s.id) === String(id)) || null;
    if (s && Number(s.stock_restant) < q) {
      setSelectedStock(null);
      setQuantityError(`Le lot "${s.produitDenomination || s.denomination || s.id}" n'a pas assez d'unités (dispo: ${s.stock_restant}).`);
      return;
    }
    setSelectedStock(s);
    setQuantityError(null);
  };

  const handleClientChange = (e) => {
    const id = e.target.value;
    const isAutre = !id || id === "autre";

    setClientAutreSelected(isAutre);

    if (isAutre) {
      setSelectedClient(null);
    } else {
      const c = clients.find((x) => String(x.id) === String(id));
      setSelectedClient(c || null);
    }
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setSaleQuantity(value);

      const q = Number(value || 0);

      if (clientAutreSelected) {
        const pu = Number(prixUnitaire || 0);
        setMontantPaye((q * pu).toFixed(2));
      }

      if (selectedProduit) {
        const totalAvailable = totalAvailableForSelectedProduit;
        if (q > totalAvailable) {
          setQuantityError(`Quantité demandée (${q}) supérieure au stock total combiné (${totalAvailable}).`);
          setDisableStockSelect(true);
          setSelectedStock(null);
        } else {
          setQuantityError(null);
          setDisableStockSelect(false);

          if (selectedStock && selectedStock.id !== "all") {
            const s = stocks.find((st) => String(st.id) === String(selectedStock.id));
            if (s && Number(s.stock_restant) < q) {
              setSelectedStock(null);
              setQuantityError(`Le lot sélectionné ne couvre pas la quantité demandée (dispo: ${s.stock_restant}).`);
            }
          }

          if (selectedStock && selectedStock.id === "all") {
            const currentSum = Object.values(multiStockQuantities).reduce((a, b) => a + Number(b || 0), 0);
            const remaining = q - currentSum;
            setRemainingToAllocate(remaining > 0 ? remaining : 0);
            if (q > 0 && currentSum < q) {
              setQuantityError(`La somme répartie (${currentSum}) est inférieure à la quantité demandée (${q}). Il reste ${remaining} à répartir.`);
            } else {
              setQuantityError(null);
            }
          }
        }
      }
    }
  };

  const handleMultiStockQuantity = (stockId, value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setMultiStockQuantities((prev) => {
        const next = { ...prev, [stockId]: value === "" ? "" : String(Number(value)) };

        const somme = Object.values(next).reduce((a, b) => a + Number(b || 0), 0);
        const q = Number(saleQuantity || 0);

        if (somme > q) {
          setQuantityError(`⚠️ Attention, vous dépassez la quantité demandée ! Demandé : ${q} — Réparti : ${somme}`);
          setRemainingToAllocate(0);
          return next;
        }

        if (q > 0 && somme < q) {
          const remaining = q - somme;
          setQuantityError(`La somme répartie (${somme}) est inférieure à la quantité demandée (${q}). Il reste ${remaining} à répartir.`);
          setRemainingToAllocate(remaining);
        } else {
          setQuantityError(null);
          setRemainingToAllocate(0);
        }

        return next;
      });
    }
  };


  const handleAddSale = async (e) => {
    e.preventDefault();

    // Remplacer submitError par la boîte de dialogue en cas d'erreur
    // Les erreurs de validation locales ci-dessous utiliseront la boîte de dialogue
    setSuccessMessage(null); // On efface le message de succès précédent
    
    const form = e.target;
    const produit_id = form.produit_id.value;
    const stock_id = form.stock_id.value;
    const quantite = Number(saleQuantity || 0);
    const date_vente = form.date.value;

    const total_vente = montantVente;
    const montant_paye_soumis = clientAutreSelected ? Number(montantPaye || 0) : null;
    const type_paiement = montant_paye_soumis === total_vente ? "cash" : null;


    if (!produit_id) return showCustomDialog('error', "Veuillez sélectionner un produit.");
    if (!stock_id) return showCustomDialog('error', "Veuillez sélectionner un stock.");
    if (!quantite || quantite <= 0) return showCustomDialog('error', "Quantité invalide.");
    if (!date_vente) return showCustomDialog('error', "Veuillez sélectionner une date.");
    if (!user?.id) return showCustomDialog('error', "Utilisateur non authentifié.");
    if (!user?.type_user) return showCustomDialog('error', "Type d’utilisateur introuvable.");

    // === RÈGLE DE VALIDATION pour client 'Autre' (Obligation de paiement total) ===
    if (clientAutreSelected && Number(montant_paye_soumis) !== total_vente) {
      return showCustomDialog('error', `Pour un client 'Autre' (anonyme), le montant payé (${montant_paye_soumis?.toFixed(2) || 0} F) doit être égal au montant de la vente (${total_vente.toFixed(2)} F). La vente est refusée.`);
    }

    let lotsToSend = [];
    if (stock_id === "all") {
      lotsToSend = Object.entries(multiStockQuantities)
        .map(([sid, val]) => ({ stock_id: sid, quantite: Number(val || 0) }))
        .filter((l) => l.quantite > 0);

      const sum = lotsToSend.reduce((a, b) => a + b.quantite, 0);
      if (sum < quantite) return showCustomDialog('error', "Quantité répartie entre lots insuffisante !");

      const totalDispo = filteredStocks.filter((s) => s.id !== "all").reduce((a, b) => a + Number(b.stock_restant), 0);
      if (quantite > totalDispo) return showCustomDialog('error', "Stock total insuffisant !");
    } else {
      const s = stocks.find((s) => String(s.id) === String(stock_id));
      if (!s) return showCustomDialog('error', "Lot introuvable.");
      if (Number(s.stock_restant) < quantite) return showCustomDialog('error', "Quantité demandée supérieure au stock disponible !");
      lotsToSend = [{ stock_id: stock_id, quantite }];
    }

    const clientToSend = selectedClient ? { id: selectedClient.id, nom: selectedClient.nom } : null;
    
    const newSale = {
      produit_id,
      lots: lotsToSend,
      date_vente,
      entrepriseId,
      siteId,
      user_id: user.id,
      user_type: user.type_user,
      client: clientToSend,
      montant_vente: total_vente,
    };

    if (clientAutreSelected) {
      newSale.montant_paye = montant_paye_soumis;
      newSale.type_paiement = type_paiement;
    }
    
    try {
      setLoadingAction(true);
      const res = await fetch(`${API_URL}/api/sales`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newSale),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Erreur lors de l’ajout de la vente");

      // Réinitialisation après succès
      form.reset();
      setSelectedProduit(null);
      setSelectedClient(null);
      setSelectedStock(null);
      setSaleQuantity("");
      setPrixUnitaire("");
      setMultiStockQuantities({});
      setQuantityError(null);
      setDisableStockSelect(false);
      setRemainingToAllocate(0);
      setMontantVente(0);
      setMontantPaye(""); 
      setClientAutreSelected(true);
      
      showCustomDialog('success', "Vente ajoutée avec succès ✅");
      setSuccessMessage("Vente ajoutée avec succès ✅"); // Garder l'affichage local du succès si non retiré

      await fetchStocks();

      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      showCustomDialog('error', err.message || "Erreur lors de l'ajout de la vente.");
    } finally {
      setLoadingAction(false);
    }
  };


  // --- Composant de dialogue personnalisé (RÉUTILISÉ) ---
  const CustomDialog = () => {
    if (!customDialog) return null;

    const { type, message } = customDialog;
    let icon, title, mainStyle, primaryButtonText;

    if (type === 'success') {
        icon = <FaCheckCircle style={{ color: '#10b981', fontSize: '2em' }} />;
        title = "Succès";
        mainStyle = { borderLeft: '5px solid #10b981' };
        primaryButtonText = "Fermer";
    } else if (type === 'error') {
        icon = <FaTimes style={{ color: '#f44336', fontSize: '2em' }} />;
        title = "Erreur";
        mainStyle = { borderLeft: '5px solid #f44336' };
        primaryButtonText = "Fermer";
    } else if (type === 'confirm') {
        icon = <FaExclamationTriangle style={{ color: '#ffb300', fontSize: '2em' }} />;
        title = "Confirmation";
        mainStyle = { borderLeft: '5px solid #ffb300' };
        primaryButtonText = "Confirmer";
    }

    return (
        <div className="modal-overlay">
            <div className="modal-content custom-dialog" style={mainStyle}>
                <button className="close-btn" onClick={closeCustomDialog}>
                    <FaTimes />
                </button>
                <div className="dialog-header">
                    {icon}
                    <h4>{title}</h4>
                </div>
                <p>{message}</p>
                <div className="modal-actions">
                    {type === 'confirm' ? (
                        <>
                            <button className="btn-secondary" onClick={closeCustomDialog}>
                                Annuler
                            </button>
                            <button className="btn-primary" onClick={handleDialogAction}>
                                {primaryButtonText}
                            </button>
                        </>
                    ) : (
                        <button className="btn-primary" onClick={closeCustomDialog}>
                            {primaryButtonText}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
  };


  return (
    <div className="sales-manager-container">
      <div className="sales-manager-header">
        <h1 className="sales-manager-title">Ajout de Ventes</h1>
      </div>

      {loadingProduits && <p>⏳ Chargement des produits...</p>}
      {loadingClients && <p>⏳ Chargement des clients...</p>}
      {loadingStocks && <p>⏳ Chargement des stocks...</p>}
      {produitsError && <p className="error">⚠️ {produitsError}</p>}
      {clientsError && <p className="error">⚠️ {clientsError}</p>}
      {stocksError && <p className="error">⚠️ {stocksError}</p>}

      <div className="sale-form-container">
        {/* L'ancien submitError n'est plus nécessaire ici car géré par CustomDialog */}
        {/* Le successMessage est conservé pour l'affichage temporaire standard (si souhaité) */}
        {successMessage && <div className="form-message success">{successMessage}</div>}

        <form className="sale-form" onSubmit={handleAddSale}>

          {/* Produit */}
          <label>Produit</label>
          <select
            name="produit_id"
            required
            disabled={loadingAction || loadingProduits}
            onChange={handleProduitChange}
            value={selectedProduit ? selectedProduit.id : ""}
          >
            <option value="">-- Sélectionnez un produit --</option>
            {produits.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nom}
              </option>
            ))}
          </select>

          {/* Prix unitaire */}
          <label>Prix unitaire (Automatique)</label>
          <div className="prix-unitaire-display">
            {prixUnitaire ? `${Number(prixUnitaire).toLocaleString('fr-FR')} F` : "— Sélectionnez produit / client —"}
          </div>

          {/* Quantité */}
          <label>Quantité totale</label>
          <input
            type="number"
            name="quantite"
            min="1"
            required
            disabled={loadingAction}
            value={saleQuantity}
            onChange={handleQuantityChange}
            placeholder="Saisissez la quantité souhaitée"
          />
          {quantityError && <div className="form-message error">{quantityError}</div>}

          {/* Stock */}
          <label>Stock / Dénomination</label>
          <select
            name="stock_id"
            required
            disabled={!selectedProduit || loadingStocks || filteredStocks.length === 0 || disableStockSelect}
            onChange={handleStockChange}
            value={selectedStock ? selectedStock.id : ""}
          >
            <option value="">-- Sélectionnez un stock --</option>
            {filteredStocks.map((s) => (
              <option key={s.id} value={s.id}>
                {s.produitDenomination || s.denomination || s.id} — Qté: {s.stock_restant}
              </option>
            ))}
          </select>

          {/* === RÉPARTITION ENTRE LOTS === */}
          {selectedStock?.id === "all" && (
            <div className="multi-stock-inputs">
              <p className="info">➡️ Répartissez la quantité entre les lots :</p>

              {filteredStocks
                .filter((s) => s.id !== "all")
                .filter((s) => Number(s.stock_restant) > 0)
                .map((s) => (
                  <div key={s.id} className="stock-entry">
                    <span>
                      {s.produitDenomination || s.denomination || s.id} ({s.stock_restant} dispo)
                    </span>
                    <input
                      type="number"
                      min="0"
                      max={s.stock_restant}
                      value={multiStockQuantities[s.id] ?? ""}
                      onChange={(e) => handleMultiStockQuantity(s.id, e.target.value)}
                      placeholder="Qté à prélever"
                    />
                  </div>
                ))}

              <div className="multi-stock-summary">
                <p>
                  Somme répartie :{" "}
                  {Object.values(multiStockQuantities).reduce((a, b) => a + Number(b || 0), 0)}
                </p>
                {Number(saleQuantity || 0) > 0 && remainingToAllocate > 0 && (
                  <p className="info">
                    La somme répartie est inférieure à la quantité demandée ({saleQuantity}). Il reste{" "}
                    <strong>{remainingToAllocate}</strong> à répartir.
                  </p>
                )}
                {Number(saleQuantity || 0) > 0 && remainingToAllocate === 0 && (
                  <p className="success">✅ Répartition complète (somme = {saleQuantity})</p>
                )}
              </div>
            </div>
          )}

          {/* Client */}
          <label>Client (optionnel)</label>
          <select
            name="client_id"
            onChange={handleClientChange}
            disabled={loadingAction || loadingClients || !selectedProduit}
            value={selectedClient ? selectedClient.id : ""}
          >
            <option value="">Autre client / Anonyme</option>
            {filteredClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom}
              </option>
            ))}
          </select>

          {/* Montant de la Vente (Affichage automatique) */}
          <label>Montant de la Vente</label>
          <div className="prix-unitaire-display total-sale-amount">
            <span style={{ fontWeight: 'bold' }}>
              {montantVente.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} F
            </span>
          </div>

          {/* Montant Payé (APPARAÎT SEULEMENT POUR CLIENT 'AUTRE') */}
          {clientAutreSelected && (
            <>
              <label>Montant Payé</label>
              <input
                type="number"
                name="montant_paye"
                step="0.01"
                required
                min={montantVente}
                disabled={loadingAction}
                value={montantPaye}
                onChange={(e) => setMontantPaye(e.target.value)}
                placeholder={`Doit payer ${montantVente.toFixed(2)} F`}
              />
              {montantVente > 0 && (
                <p className={`form-hint ${Number(montantPaye) === montantVente ? 'success' : 'error'}`}>
                  Client Anonyme: Montant Payé doit être {montantVente.toLocaleString('fr-FR')} F.
                </p>
              )}
            </>
          )}

          {/* Date */}
          <label>Date</label>
          <input
            type="date"
            name="date"
            required
            disabled={loadingAction}
            max={new Date().toISOString().split("T")[0]}
          />

          <div className="form-actions">
            <button
              type="submit"
              className="save-btn"
              disabled={
                loadingAction ||
                Boolean(quantityError) ||
                (clientAutreSelected && Number(montantPaye) !== montantVente)
              }
            >
              {loadingAction ? (<FaSpinner className="spin" />) : (<><FaMoneyBillWave /> Enregistrer</>)}
            </button>
          </div>

        </form>
      </div>

      <CustomDialog />
    </div>
  );
}